// ---------------------------------------------------------------------------
// /campaigns/browser-targeting — route (OPUS-D10 / feature #70).
//
// Renders the BrowserTargetingForm inside the dashboard shell. Exports both
// a plain React component (for ad-hoc embedding + file-based routing) and a
// `buildRoute(parent)` factory so the scaffold's programmatic router tree
// can mount this page without shared-file edits.
//
// Styling per CLAUDE.md dashboard brief:
//   - light theme only
//   - emerald -> sky brand gradient on the page heading
//   - Inter, 6-8px rounded corners, zinc borders, subtle shadow
//   - no emojis, no competitor names
// ---------------------------------------------------------------------------

import { type AnyRoute, createRoute } from "@tanstack/react-router";
import { BrowserTargetingForm } from "@/features/browser-targeting/BrowserTargetingForm";

/**
 * Factory used by the root router to mount this page under its parent route.
 * Kept dependency-free of the router module's ambient Register so we never
 * couple agents to the shared router.tsx assembly order.
 */
export function buildBrowserTargetingRoute<TParent extends AnyRoute>(
	parent: TParent,
) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns/browser-targeting",
		component: BrowserTargetingPage,
	});
}

export function BrowserTargetingPage() {
	return (
		<main className="mx-auto w-full max-w-[960px] px-6 py-10">
			<header className="mb-6 flex flex-col gap-2">
				<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
					Browser targeting
				</h1>
				<p className="text-sm text-[color:var(--color-text-muted)]">
					Restrict a campaign to specific browser families. Leave the rule
					empty to accept every browser — new campaigns are open by default.
				</p>
			</header>

			<BrowserTargetingForm />

			<footer className="mt-6 text-xs text-[color:var(--color-text-muted)]">
				Changes apply immediately after save. Classification runs on the
				request user-agent; the "Other" bucket catches headless and
				legacy clients.
			</footer>
		</main>
	);
}

export default BrowserTargetingPage;
